import Axios from "axios";
import { routerMiddleware } from "connected-react-router";
import { History } from "history";
import qs from "query-string";
import { applyMiddleware, createStore, Store } from "redux";
import { composeWithDevTools } from "redux-devtools-extension/logOnlyInProduction";
import createSagaMiddleware, { END } from "redux-saga";

import configService from "../config/ConfigService";
import localSettings from "../config/LocalSettings";
import Environment from "../enum/Environment";

import { setApi, setReadonlyApi } from "./api/actions";
import ApiService from "./api/ApiService";
import { changeLocale } from "./locale/actions";
import createRootReducer, { RootState } from "./rootReducer";
import { rootSaga } from "./rootSaga";
import { changeTheme } from "./theme/actions";

const apiHttpClient = Axios.create({
  baseURL: configService.getBaseApiUrl(),
  paramsSerializer: (params) => qs.stringify(params, { arrayFormat: "none" }),
  timeout: configService.getDefaultApiTimeout(),
});

const readonlyApiHttpClient = Axios.create({
  baseURL: configService.getBaseReadonlyApiUrl(),
  paramsSerializer: (params) => qs.stringify(params, { arrayFormat: "none" }),
  timeout: configService.getDefaultApiTimeout(),
});

const createApiService = (ApiServiceClass: typeof ApiService) => {
  return new ApiServiceClass(apiHttpClient);
};

const createReadonlyApiService = (ApiServiceClass: typeof ApiService) => {
  return new ApiServiceClass(readonlyApiHttpClient);
};

// @ts-ignore
const logger = (store) => (next) => (action) => {
  console.group(action.type);
  console.log("dispatching", action);
  const result: any = next(action);
  console.log("next state", store.getState());
  console.groupEnd();
  return result;
};

const configureReduxStore = (history: History): Store<RootState> => {
  const sagaMiddleware = createSagaMiddleware();

  const composeEnhancers = composeWithDevTools({
    trace:
      !!process.env.REACT_APP_REDUX_TRACE &&
      process.env.REACT_APP_ENVIRONMENT !== Environment.PRODUCTION,
    traceLimit: 30, // default 10 is sometimes not enough
  });

  const store = createStore(
    createRootReducer(history),
    undefined,
    composeEnhancers(
      process.env.REACT_APP_REDUX_USE_CONSOLE_LOGGER
        ? applyMiddleware(logger, sagaMiddleware, routerMiddleware(history))
        : applyMiddleware(sagaMiddleware, routerMiddleware(history))
    )
  );

  sagaMiddleware.run(rootSaga);
  store.dispatch(setApi(createApiService(ApiService)));
  store.dispatch(setReadonlyApi(createReadonlyApiService(ApiService)));

  // Switch locale and theme. We read values from LocalSettings so that user can have a language and
  // a theme of his preference even before he signs in.
  store.dispatch(changeLocale.request(localSettings.getLocale())); // update locale
  store.dispatch(changeTheme.request(localSettings.getTheme())); // update theme

  if (module.hot) {
    module.hot.accept("./rootReducer", () => {
      // eslint-disable-next-line @typescript-eslint/no-require-imports
      store.replaceReducer(require("./rootReducer").default(history));
    });
    module.hot.accept("./rootSaga", () => {
      store.dispatch(END);
      // eslint-disable-next-line @typescript-eslint/no-require-imports
      sagaMiddleware.run(require("./rootSaga").default);
    });
    module.hot.accept("./api/ApiService", () => {
      // eslint-disable-next-line @typescript-eslint/no-require-imports
      store.dispatch(setApi(createApiService(require("./api/ApiService").default)));
      // eslint-disable-next-line @typescript-eslint/no-require-imports
      store.dispatch(setReadonlyApi(createReadonlyApiService(require("./api/ApiService").default)));
    });
  }

  return store;
};

export type AppDispatch = ReturnType<typeof configureReduxStore>["dispatch"];
export default configureReduxStore;
